import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  List,
  ListSubheader,
  Popover,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/system';
import LanternalLogo from 'src/components/LanternalLogo';
import useAuth from 'src/hooks/useAuth';
import { ALERT_BADGE_PULSE_INTERVAL } from 'src/constants';
import { getApiEnvironment } from 'src/utilities/api-config';
import useSettings from 'src/hooks/useSettings';
import { THEMES } from 'src/constants';
import { capitalCase } from 'change-case';
import { AlertsDisplay, useAlerts } from 'src/components/Alerts';
import { useSnackbar } from 'notistack';
import CableIcon from '@mui/icons-material/Cable';
import FlagIcon from '@mui/icons-material/Flag';
import HandymanIcon from '@mui/icons-material/Handyman';
import HubIcon from '@mui/icons-material/Hub';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import UpdateIcon from '@mui/icons-material/Update';
import UploadIcon from '@mui/icons-material/Upload';
import { useRole } from 'src/contexts/RoleContext';
import { useSelectedClients } from 'src/contexts/SelectedClientsContext';
import { renderNavItems } from 'src/utilities/NavBarUtilities';
import { LanRounded, ManageHistoryRounded, PeopleRounded, WorkRounded } from '@mui/icons-material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ImportNewClientDialog from 'src/components/ImportNewClientDialog';
import { NavItemStatusProvider, useNavItemStatuses } from 'src/contexts/NavItemStatusContext';
import { BUILD_INFO } from 'src/buildInfo';

const drawerWidth = 300;

const getSections = (routes) => {
  const sections = [
    {
      subheader: 'Client',
      items: [
        {
          title: 'Profile',
          icon: WorkRounded,
          route: routes.app.profile.route,
        },
        {
          title: 'Team',
          icon: PeopleRounded,
          route: routes.app.team.route,
        },
        {
          title: 'Data Integrations',
          icon: LanRounded,
          route: routes.app.dataIntegrations.route,
        },
        {
          title: 'Forecast',
          icon: UpdateIcon,
          statusKey: 'forecast',
          route: routes.app.forecastRoundtableTool.route,
        },
        {
          title: 'Goals Tracker',
          icon: FlagIcon,
          route: routes.app.clientGoalsTracker.route,
        },
        {
          title: 'Ember Dataset Manager',
          icon: StorageIcon,
          route: routes.app.emberDatasetManager.route,
        },
      ],
    },
    {
      subheader: 'Global',
      items: [
        {
          title: 'Client Attribution Tool',
          icon: HubIcon,
          route: routes.app.clientAttributionTool.route,
        },
        {
          title: 'Permission Management',
          icon: PersonIcon,
          route: routes.app.permissions.route,
        },
        {
          title: 'Foreman-NetSuite Mapping',
          icon: CableIcon,
          route: routes.app.foremanNetsuiteMappingTool.route,
        },
        {
          title: 'NetSuite Client Mapping',
          icon: LinkIcon,
          route: routes.app.netsuiteClientMapping.route,
        },
      ],
    },
    {
      subheader: 'Administration',
      items: [
        {
          title: 'Forecast Roundtable Manager',
          icon: ManageHistoryRounded,
          route: routes.app.forecastRoundtableManager.route,
        },
        {
          title: 'Client Data Manager',
          icon: HandymanIcon,
          route: routes.app.clientManager.route,
        },
        {
          title: 'Client Attribution Manager',
          icon: SettingsIcon,
          route: routes.app.clientAttributionManager.route,
        },
        {
          title: 'PBCS File Uploader',
          icon: UploadIcon,
          route: routes.app.pbcsFileUploader.route,
        },
        {
          title: 'Harvester Status',
          icon: SyncIcon,
          route: routes.app.harvesterStatus.route,
        },
      ],
    },
  ];

  return sections
    .map((section) => {
      const items = section.items
        .map((item) => {
          if (!item.route) {
            return null;
          }

          const childItems = item.items ? item.items.filter((child) => child.route?.accessible) : undefined;

          if (childItems && childItems.length === 0) {
            return null;
          }

          if (!item.route.accessible) {
            return null;
          }

          return {
            ...item,
            ...(childItems ? { items: childItems } : {}),
          };
        })
        .filter(Boolean);

      return { ...section, items };
    })
    .filter((section) => section.items.length);
};

const FORECAST_STATUS_KEY = 'forecast';
const STATUS_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

// Pulse animation for alert badge - pulses briefly every ALERT_BADGE_PULSE_INTERVAL seconds
// Uses modern 'scale' property to avoid interfering with MUI's transform positioning
const createPulseAnimation = (intervalSeconds) => {
  const pulseDuration = 1; // 1 second pulse
  const pulsePercent = (pulseDuration / intervalSeconds) * 100;
  const startPulse = 100 - pulsePercent;
  const midPulse = 100 - pulsePercent * 0.5;

  return keyframes`
    0%, ${startPulse}% {
      scale: 1;
      box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
    }
    ${midPulse}% {
      scale: 1.2;
      box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.4);
    }
    100% {
      scale: 1;
      box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
    }
  `;
};

const pulse = createPulseAnimation(ALERT_BADGE_PULSE_INTERVAL);

const buildForecastStatusResolver = (selectedClients) => ({
  key: FORECAST_STATUS_KEY,
  resolve: async () => {
    if (!selectedClients || selectedClients.length !== 1) {
      return null;
    }

    try {
      const parentId = selectedClients[0].id;
      const resp = await LanternApi.getClientAttributionParentClientWithClientContracts(parentId);
      if (!resp?.success) return null;
      const parent = resp.body?.parentClient || {};
      const contracts = resp.body?.clientContracts || [];
      const parentHas = !!parent.netsuiteIdGeneral;
      const withId = contracts.filter((c) => !!c.netsuiteId).length;
      const withoutId = contracts.length - withId;

      if (!parentHas && withId === 0) {
        return { type: 'error', blocked: true, tooltip: 'Contact finance, this client has no NetSuite IDs' };
      }
      if (!parentHas || withoutId > 0) {
        return { type: 'warning', blocked: false, tooltip: 'Contact finance, this client is missing a NetSuite ID' };
      }

      return null;
    } catch (e) {
      return { type: 'warning', blocked: false, tooltip: 'Unable to evaluate status right now' };
    }
  },
});

// Template for adding a new status-driven nav item:
// 1) Give the nav item a statusKey in getSections (e.g., statusKey: 'client-data-checks').
// 2) Add a resolver here:
// const buildClientDataStatusResolver = (deps) => ({
//   key: 'client-data-checks',
//   resolve: async () => {
//     // call API / evaluate client selection, return { type: 'warning'|'error', tooltip, blocked? } or null when OK
//     return null;
//   },
// });
// 3) Add the resolver to the array in NavBar (see resolvers below). The status will auto-inject via renderNavItems.

function NavBarContent({ onNavClose, openMobile, routes }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { selectedRole, setSelectedRole, ROLES } = useRole();
  const { availableClients, selectedClients, setSelectedClients, loadingAvailableClients } = useSelectedClients();
  const { enqueueSnackbar } = useSnackbar();
  const { settings, saveSettings } = useSettings();
  const [settingsValues, setSettingsValues] = React.useState({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });
  const handleSettingsChange = (field, value) => setSettingsValues((v) => ({ ...v, [field]: value }));
  const handleSaveSettings = () => saveSettings(settingsValues);

  // Alerts
  const { alerts, archivedAlerts, archiveAlert, unarchiveAlert, isArchiving } = useAlerts(isAuthenticated);

  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);
  const [userMenuTab, setUserMenuTab] = React.useState('settings');
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const { statuses } = useNavItemStatuses();

  const roleOptions = Object.values(ROLES);

  const allClientsOption = React.useMemo(() => ({ id: 0, name: 'All Clients' }), []);
  const clientOptions = React.useMemo(
    () => [allClientsOption, ...(availableClients || [])],
    [allClientsOption, availableClients],
  );
  const selectedClientValue = selectedClients && selectedClients.length === 1 ? selectedClients[0] : allClientsOption;

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Top anchored header */}
      <Box px={2} pt={1} pb={0.5} sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="start"
          gap={1}
          width={1}
          onClick={() => navigate('/')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') navigate('/');
          }}
          role="button"
          tabIndex={0}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <LanternalLogo environment={getApiEnvironment()} />
          <Typography variant="h4" color="text.primary" sx={{ fontWeight: 900, textAlign: 'center', marginLeft: 1.5 }}>
            Console
          </Typography>
        </Box>
        {/* Role selector moved to settings popover */}
        {/* Client selector in header (single-select with All Clients) */}
        <Box mt={1}>
          <Autocomplete
            size="small"
            options={clientOptions}
            value={selectedClientValue}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(opt, val) => (opt?.id ?? null) === (val?.id ?? null)}
            onChange={(e, newValue) => {
              if (!newValue || newValue.id === 0) {
                setSelectedClients([]); // All Clients
              } else {
                setSelectedClients([newValue]);
              }
            }}
            loading={loadingAvailableClients}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select client" />}
          />
        </Box>
      </Box>

      {/* Middle scrollable section */}
      <Box pt={1} px={2} pb={2} sx={{ overflowY: 'auto', flex: 1 }}>
        {(() => {
          const sections = getSections(routes);
          return sections.map((section, idx) => (
            <React.Fragment key={section.subheader || idx}>
              <List
                dense
                disablePadding
                subheader={
                  <ListSubheader
                    disableSticky
                    disableGutters
                    sx={{
                      fontWeight: 'bold',
                      px: 0,
                      py: 0.5,
                      mt: idx === 0 ? 0 : 0.5,
                      mb: 0.5,
                      lineHeight: 1.2,
                      fontSize: 13,
                      color: 'text.secondary',
                    }}
                  >
                    {section.subheader}
                  </ListSubheader>
                }
              >
                {section.subheader === 'Global' && (
                  <Box
                    onClick={(e) => {
                      e.preventDefault();
                      setImportDialogOpen(true);
                    }}
                  >
                    {renderNavItems({
                      items: [
                        {
                          title: 'Add Parent Client',
                          icon: PersonAddAlt1Icon,
                          route: { accessible: true, path: '#' },
                        },
                      ],
                      pathname: location.pathname,
                      expanded: openMobile,
                      statuses,
                    })}
                  </Box>
                )}
                {renderNavItems({
                  items: section.items,
                  pathname: location.pathname,
                  expanded: openMobile,
                  statuses,
                })}
              </List>
              {idx < sections.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ));
        })()}
      </Box>

      {/* Bottom anchored user area */}
      {isAuthenticated && (
        <Box px={2} py={1.5} sx={{ borderTop: (t) => `1px solid ${t.palette.divider}` }}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            onClick={(e) => {
              setUserMenuAnchor(e.currentTarget);
              setUserMenuOpen(true);
              // Default to alerts tab if there are alerts, otherwise settings
              setUserMenuTab(alerts.length > 0 ? 'alerts' : 'settings');
            }}
            sx={{ cursor: 'pointer' }}
          >
            <Badge
              color="error"
              badgeContent={alerts.length}
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={
                alerts.length > 0
                  ? {
                      '& .MuiBadge-badge': {
                        animation: `${pulse} ${ALERT_BADGE_PULSE_INTERVAL}s ease-in-out infinite`,
                        transformOrigin: 'center center',
                      },
                    }
                  : {}
              }
            >
              <Avatar src={user?.avatar} alt={user?.displayName} sx={{ width: 36, height: 36 }} />
            </Badge>
            <Box flex={1} minWidth={0}>
              <Typography variant="body1" noWrap>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.userPrincipalName || ''}
              </Typography>
            </Box>
          </Box>
          <Popover
            open={userMenuOpen}
            onClose={() => setUserMenuOpen(false)}
            anchorEl={userMenuAnchor}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 2, width: 405 } } }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Avatar src={user?.avatar} sx={{ width: 32, height: 32 }} />
              <Box>
                <Typography variant="subtitle1">{user?.displayName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.userPrincipalName}
                </Typography>
              </Box>
            </Box>
            <Tabs value={userMenuTab} onChange={(e, v) => setUserMenuTab(v)}>
              <Tab value="settings" label="Settings" />
              <Tab value="alerts" label={`Alerts (${alerts.length})`} />
            </Tabs>
            <Box mt={2}>
              {userMenuTab === 'settings' && (
                <Box
                  sx={{
                    maxHeight: '330px',
                    minHeight: '330px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    scrollbarGutter: 'stable',
                    pt: 1,
                  }}
                >
                  <Box mb={2}>
                    <Autocomplete
                      size="small"
                      value={selectedRole}
                      onChange={(event, newValue) => {
                        if (newValue) setSelectedRole(newValue);
                      }}
                      options={roleOptions}
                      renderInput={(params) => <TextField {...params} label="Role" variant="outlined" />}
                    />
                  </Box>
                  <Box mt={1}>
                    <TextField
                      fullWidth
                      label="Theme"
                      name="theme"
                      onChange={(e) => handleSettingsChange('theme', e.target.value)}
                      select
                      SelectProps={{ native: true }}
                      value={settingsValues.theme}
                      variant="outlined"
                    >
                      {Object.keys(THEMES).map((theme) => (
                        <option key={theme} value={theme}>
                          {capitalCase(theme)}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settingsValues.responsiveFontSizes}
                          onChange={(e) => handleSettingsChange('responsiveFontSizes', e.target.checked)}
                        />
                      }
                      label="Responsive font sizes"
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      Build Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Commit: {BUILD_INFO.gitCommitShortHash} ({BUILD_INFO.gitBranch})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Build: {BUILD_INFO.buildNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Time: {new Date(BUILD_INFO.buildTimestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box mt="auto" display="flex" justifyContent="flex-end" gap={1}>
                    <Button variant="contained" onClick={handleSaveSettings}>
                      Save Settings
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          await logout();
                        } catch {
                          enqueueSnackbar('Unable to logout', { variant: 'error' });
                        }
                      }}
                      variant="contained"
                      color="inherit"
                    >
                      Logout
                    </Button>
                  </Box>
                </Box>
              )}
              {userMenuTab === 'alerts' && (
                <Box
                  sx={{
                    maxHeight: '330px',
                    minHeight: '330px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    scrollbarGutter: 'stable',
                    pt: 1,
                  }}
                >
                  <AlertsDisplay
                    alerts={alerts}
                    archivedAlerts={archivedAlerts}
                    archiveAlert={archiveAlert}
                    unarchiveAlert={unarchiveAlert}
                    isArchiving={isArchiving}
                  />
                </Box>
              )}
            </Box>
          </Popover>
        </Box>
      )}
    </Box>
  );

  return (
    <React.Fragment>
      {isAuthenticated && (
        <Box sx={{ display: 'flex' }}>
          <Drawer
            anchor="left"
            sx={[
              {
                width: drawerWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              },
              openMobile
                ? {
                    width: drawerWidth,
                    transition: (theme) =>
                      theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                  }
                : {
                    transition: (theme) =>
                      theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                    overflowX: 'hidden',
                    width: '5px',
                    '&:hover': {
                      width: drawerWidth,
                      transition: (theme) =>
                        theme.transitions.create('width', {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.enteringScreen,
                        }),
                      transitionDelay: '200ms',
                    },
                  },
            ]}
            slotProps={{
              paper: {
                sx: [
                  openMobile
                    ? {
                        width: drawerWidth,
                        transition: (theme) =>
                          theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                          }),
                      }
                    : {
                        transition: (theme) =>
                          theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                          }),
                        overflowX: 'hidden',
                        width: '5px',
                        '&:hover': {
                          width: drawerWidth,
                          transition: (theme) =>
                            theme.transitions.create('width', {
                              easing: theme.transitions.easing.sharp,
                              duration: theme.transitions.duration.enteringScreen,
                            }),
                          transitionDelay: '200ms',
                        },
                      },
                ],
              },
            }}
            open={openMobile}
            variant="permanent"
          >
            {content}
          </Drawer>
          <ImportNewClientDialog
            open={importDialogOpen}
            onClose={() => setImportDialogOpen(false)}
            onSubmit={(client) => {
              setImportDialogOpen(false);
              enqueueSnackbar(`Import requested for: ${client?.name}`, { variant: 'success' });
              // TODO: integrate import flow
            }}
          />
        </Box>
      )}
    </React.Fragment>
  );
}

export default function NavBar(props) {
  const { selectedClients } = useSelectedClients();

  const forecastResolver = React.useMemo(() => buildForecastStatusResolver(selectedClients), [selectedClients]);
  const resolvers = React.useMemo(() => [forecastResolver], [forecastResolver]);

  return (
    <NavItemStatusProvider resolvers={resolvers} dependencies={[selectedClients]} refreshIntervalMs={STATUS_REFRESH_INTERVAL_MS}>
      <NavBarContent {...props} />
    </NavItemStatusProvider>
  );
}
